import { useEffect, useState } from "react";
import { Loader2, MapPin, UserCheck, FileText, Wallet, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { fdate } from "@/lib/format";

const ETAPA_META = {
  Lead: { icon: MapPin, color: "text-sky-600", bg: "border-sky-200 bg-sky-50" },
  Prospecto: { icon: FileText, color: "text-amber-600", bg: "border-amber-200 bg-amber-50" },
  Cliente: { icon: UserCheck, color: "text-forest-600", bg: "border-forest-200 bg-forest-50" },
  Cartera: { icon: Wallet, color: "text-violet-600", bg: "border-violet-200 bg-violet-50" },
};

const TIPO_LABEL = {
  prospecto_creado: "Prospecto creado",
  lead_a_prospecto: "Lead → Prospecto",
  prospecto_a_cliente: "Prospecto → Cliente",
  asignacion_cartera: "Asignación de cartera",
  reasignacion_cartera: "Reasignación de cartera",
  reasignacion_originador: "Reasignación de originador",
};

export function MovimientosTimeline({ clienteId, reloadKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let on = true;
    setLoading(true);
    api.get(`/clientes/${clienteId}/movimientos`)
      .then((r) => { if (on) setData(r.data); })
      .catch(() => { if (on) setData(null); })
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [clienteId, reloadKey]);

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;
  if (!data) return <p className="py-8 text-center text-sm text-slate-400">No se pudo cargar el historial.</p>;

  const tl = data.timeline || [];
  return (
    <div data-testid="c360-movimientos" className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm">
          <Clock className="h-4 w-4 text-slate-500" />
          <span className="text-slate-500">Tiempo de ciclo:</span>
          <span className="font-semibold text-slate-800" data-testid="c360-tiempo-ciclo">
            {data.tiempo_ciclo_dias == null ? "—" : `${data.tiempo_ciclo_dias} día(s)`}
          </span>
        </div>
        <p className="text-xs text-slate-500">
          Originador: <span className="font-medium text-slate-700">{data.originador_actual}</span>
          {" · "}Responsable: <span className="font-medium text-slate-700">{data.responsable_actual}</span>
        </p>
      </div>

      <ol className="relative ml-3 border-l-2 border-slate-200" data-testid="c360-timeline">
        {tl.map((s, i) => {
          const meta = ETAPA_META[s.etapa] || ETAPA_META.Cliente;
          const Icon = meta.icon;
          return (
            <li key={i} className="mb-5 ml-5" data-testid={`c360-etapa-${s.etapa.toLowerCase()}`}>
              <span className={`absolute -left-[11px] flex h-5 w-5 items-center justify-center rounded-full border ${meta.bg}`}>
                <Icon className={`h-3 w-3 ${meta.color}`} />
              </span>
              <div className="flex flex-wrap items-baseline gap-x-2">
                <p className="text-sm font-semibold text-slate-800">{s.etapa}</p>
                {s.pendiente && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">Sin asignar</span>}
                {s.dias_en_etapa != null && <span className="text-[11px] text-slate-400">{s.dias_en_etapa} día(s) en etapa</span>}
              </div>
              <p className="text-xs text-slate-500">Responsable: <span className="font-medium text-slate-600">{s.responsable}</span></p>
              <p className="text-[11px] text-slate-400">{s.fecha ? fdate(s.fecha) : "—"}</p>
            </li>
          );
        })}
      </ol>

      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Eventos registrados</p>
        {(!data.eventos || !data.eventos.length) ? (
          <p className="py-4 text-center text-sm text-slate-400" data-testid="c360-sin-eventos">Aún no hay movimientos registrados para este cliente.</p>
        ) : (
          <div className="space-y-1.5" data-testid="c360-eventos">
            {data.eventos.map((e, i) => (
              <div key={i} className="flex flex-wrap items-center gap-x-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs">
                <span className="font-medium text-slate-700">{TIPO_LABEL[e.tipo_evento] || e.tipo_evento}</span>
                {(e.de_nombre || e.a_nombre) && <span className="text-slate-500">{e.de_nombre || "—"} → {e.a_nombre || "—"}</span>}
                <span className="text-slate-400">· {e.por || "Sistema"} · {fdate(e.fecha)}</span>
                {e.motivo && <span className="w-full text-[11px] text-slate-400">Motivo: {e.motivo}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
